import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchEmployees } from "../../api/users";

/**
 * Marketing hero from Airon.html design (Anthropic design handoff).
 * The h1 rotates between three Airon-taglines with a soft fade/slide.
 */
const HERO_HEADLINES = [
  ["Frisk luft.", "Sunne bygg.", "Friskere folk."],
  ["Vi tar hånd om", "luften du puster."],
  ["Godt inneklima.", "Hver dag.", "Hele året."],
];
const ROTATE_MS = 5000;
const FADE_MS = 450;

export function DesignHero() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [employeeCount, setEmployeeCount] = useState(null);

  // Hent antall ansatte fra API — samme kilde som /om-oss.
  useEffect(() => {
    let cancelled = false;
    async function loadEmployeeCount() {
      try {
        const res = await fetchEmployees({ per_page: 100, fields: "id" });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setEmployeeCount(list.length > 0 ? list.length : null);
        }
      } catch (e) {
        console.error("Kunne ikke hente antall ansatte:", e);
        if (!cancelled) setEmployeeCount(null);
      }
    }
    loadEmployeeCount();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (HERO_HEADLINES.length < 2) return undefined;
    // Respect reduced-motion: skip the rotation for users who prefer it.
    const mq =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    if (mq && mq.matches) return undefined;

    const id = setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % HERO_HEADLINES.length);
        setVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  const lines = HERO_HEADLINES[index];

  return (
    <section className="hero">
      <div className="wrap hero-inner">
        <div className="hero-grid">
          <div>
            <div className="eyebrow">
              <span className="dot" />
              Ventilasjon · Inneklima · Service
            </div>
            <h1
              className="display"
              aria-live="polite"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
                transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
                // Reserve space for the tallest (3-line) variant to avoid layout shift.
                minHeight: "3em",
              }}
            >
              {lines.map((line, i) => {
                const isLast = i === lines.length - 1;
                return (
                  <span key={i}>
                    {isLast ? <em>{line}</em> : line}
                    {!isLast && <br />}
                  </span>
                );
              })}
            </h1>
            <p className="hero-sub">
              Airon leverer ventilasjonsløsninger til bolig, næring og industri — prosjektert,
              installert og vedlikeholdt av et lite, fagkyndig team som bryr seg om hva du puster
              inn.
            </p>
            <div className="hero-actions">
              <Link to="/kontakt" className="btn">
                Be om befaring <span className="arr">→</span>
              </Link>
              <a href="#tjenester" className="btn ghost">
                Se tjenestene våre
              </a>
            </div>
            <div className="hero-trust">
              {employeeCount !== null && employeeCount > 0 ? (
                <div className="item">
                  <span className="n">{employeeCount}</span>
                  <span className="l">Fagfolk i teamet</span>
                </div>
              ) : null}
              <div className="item">
                <span className="n">20+</span>
                <span className="l">År i bransjen</span>
              </div>
              <div className="item">
                <span className="n">2019</span>
                <span className="l">Stiftet i mai</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hv-main">
              <svg
                className="fan"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="50" cy="50" r="46" stroke="var(--airon)" strokeWidth="2" fill="none" />
                <g fill="var(--airon)">
                  <path d="M50 50 C 40 32, 30 28, 22 30 C 26 38, 36 46, 50 50Z" />
                  <path d="M50 50 C 56 32, 66 20, 74 22 C 72 32, 64 44, 50 50Z" />
                  <path d="M50 50 C 66 48, 78 56, 80 66 C 70 66, 58 60, 50 50Z" />
                  <path d="M50 50 C 48 66, 40 78, 28 80 C 30 70, 38 58, 50 50Z" />
                  <path
                    d="M50 50 C 34 54, 20 50, 16 40 C 26 38, 40 42, 50 50Z"
                    opacity="0.85"
                  />
                </g>
                <circle cx="50" cy="50" r="5" fill="#fff" stroke="var(--airon)" strokeWidth="2" />
              </svg>
            </div>
            <div className="hv-label tl">LIVE · Bygg 3 · Sunndal</div>
            <div className="hv-label br">Illustrasjon</div>

            <div className="hv-card hv-card-1">
              <div className="k">CO₂-nivå</div>
              <div className="v">
                612<span className="unit">ppm</span>
              </div>
              <div className="bar">
                <span style={{ width: "45%" }} />
              </div>
            </div>
            <div className="hv-card hv-card-2">
              <div className="k">Luftveksling</div>
              <div className="v">
                2.4<span className="unit">× / time</span>
              </div>
              <div className="bar">
                <span style={{ width: "72%" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
